import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import { faPencil } from '@soundxyz/font-awesome/pro-light-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { FeaturedSoundOverlay } from '@components/Profile/FeaturedSoundOverlay';
import { SLogoFeaturedSoundOverlay } from '@components/Profile/SLogoFeaturedSoundOverlay';
import type { FeedPostModalMainSoundFragment } from '@graphql/reactQuery';
import { useGetDominantBackgroundColor } from '@hooks/useGetDominantBackgroundColor';
import { useHover } from '@hooks/useHover';
import { DateTimeUtils } from '@utils/dateTime';
import { getStaticCoverImage } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedPostModalMainSound on Release {
    ...ReleaseCoverImagePlayButton
    totalMinted
    track {
      duration
      title
      id
    }
    id
    coverImage {
      id
      url
      dominantColor
    }
    staticCoverImage {
      id
      url
      dominantColor
    }
    artist {
      id
      name
    }
  }
`);

export function FeedPostModalMainSound({
  release,
  onEditSoundClicked,
}: {
  release: FeedPostModalMainSoundFragment;
  onEditSoundClicked: () => void;
}) {
  const coverImageUrl = getStaticCoverImage({
    coverImage: release.coverImage.url,
    staticCoverImage: release.staticCoverImage?.url,
  });

  const [hovering, hoverProps] = useHover();

  const dominantBackgroundColor = useGetDominantBackgroundColor({
    dominantColor: release.staticCoverImage?.dominantColor || release.coverImage.dominantColor,
  });

  return (
    <div
      className="relative overflow-hidden rounded-lg p-4"
      style={{
        backgroundColor: dominantBackgroundColor,
      }}
      {...hoverProps}
    >
      <div className="absolute left-0 top-0 w-full overflow-hidden">
        <FeaturedSoundOverlay className="relative h-full w-full overflow-hidden" />
      </div>
      <div className="z-0 absolute inset-0 overflow-hidden">
        <SLogoFeaturedSoundOverlay className="h-full w-full scale-[2.4]" />
      </div>

      <div className="z-1 relative flex items-center gap-3">
        <div className="relative aspect-square w-16 flex-00auto overflow-hidden rounded-md bg-neutral100">
          <Image src={coverImageUrl} fill alt={release.track.title} className="object-fill" />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="line-clamp-1 text-base-s text-white">{release.artist.name}</div>
          <div className="line-clamp-1 font-title text-title-m font-medium text-white">
            {release.track.title}
          </div>
          <div className="flex items-center gap-2 text-base-s text-white opacity-60">
            <div>{`${release.totalMinted} Mints`}</div>
            <div>•</div>
            <div>{DateTimeUtils.formatDurationToMS(release.track.duration)}</div>
          </div>
        </div>
        {hovering && (
          <div className="flex-00auto cursor-pointer p-1" onClick={onEditSoundClicked}>
            <FontAwesomeIcon icon={faPencil} size="lg" className="text-white" />
          </div>
        )}
      </div>
    </div>
  );
}
